import type { OrganizationQuotas, QuotaBudget, ServerAgentStatus } from "@t3tools/contracts";

import type { InferenceCredentialStatus } from "../../environments/primary/inferenceCredential";
import { i18n } from "../../i18n";
import { formatQuotaRenewal } from "../../quotaPresentation";
import { failureMessageKey } from "./inferenceFailureMessages";

const agentMessages = {
  monthly: ["chat/monthlyAgentLimitReached_gvo8dd", "chat/monthlyAgentLimitReached_9s8yae"],
  five_hour: ["chat/n5HourAgentLimit_l8s29b", "chat/n5HourAgentLimit_fo6f8h"],
  weekly: ["chat/weeklyAgentLimitReached_7hh41t", "chat/weeklyAgentLimitReached_jn00bl"],
} as const;

function resetTime(budget: QuotaBudget) {
  const time = budget.resetsAt === null ? NaN : Date.parse(budget.resetsAt);
  // An unknown reset keeps its warning until a new snapshot supplies a date or clears it.
  return Number.isNaN(time) ? Infinity : time;
}

function holdingBudget(budgets: readonly QuotaBudget[], nowMs: number) {
  return budgets
    .filter((budget) => budget.exhausted && resetTime(budget) > nowMs)
    .toSorted((a, b) => resetTime(b) - resetTime(a))[0];
}

export function composerStatusItems({
  quotas,
  agent,
  credential,
  operatorEntry = false,
  locale,
  nowMs,
}: {
  quotas: OrganizationQuotas | null;
  agent: ServerAgentStatus | null;
  credential: InferenceCredentialStatus;
  /** An operator's read-only entry, whose key is refused by design rather than by its subscription. */
  operatorEntry?: boolean;
  locale: string;
  nowMs: number;
}) {
  const t = i18n.getFixedT(locale);
  const items: { id: string; text: string }[] = [];
  const agentBudget = holdingBudget(quotas?.agent.budgets ?? [], nowMs);
  if (agentBudget) {
    const date = formatQuotaRenewal(agentBudget.resetsAt, agentBudget.kind, locale, nowMs);
    const messages = agentMessages[agentBudget.kind];
    items.push({ id: "agent_quota", text: date ? t(messages[1], { date }) : t(messages[0]) });
  }
  const ocrBudget = holdingBudget(quotas?.ocr.budgets ?? [], nowMs);
  if (ocrBudget) {
    const date = formatQuotaRenewal(ocrBudget.resetsAt, "monthly", locale, nowMs);
    items.push({
      id: "ocr_quota",
      text: date
        ? t("chat/documentRecognitionLimitReached_p1pocb", { date })
        : t("chat/documentRecognitionLimitReached_ag40yi"),
    });
  }
  if (quotas?.storage?.level === "exhausted") {
    items.push({ id: "storage_quota", text: t("chat/workspaceStorageIsFull_g06od0") });
  } else if (quotas?.storage?.level === "warning") {
    items.push({ id: "storage_nearly_full", text: t("chat/workspaceStorageIsNearly_rue41e") });
  }
  if (agent && agent.status !== "ready" && agent.status !== "disabled") {
    const message =
      agent.status === "warning"
        ? "chat/theAgentHasLimited_mq61rp"
        : agent.auth.status === "unauthenticated"
          ? "chat/theAgentIsNot_6xu6am"
          : "chat/theAgentIsUnavailable_m9nod6";
    items.push({
      id: "agent_status",
      text: `${t(message)}. ${t("chat/contactYourAdministratorIf_ajdz0e")}`,
    });
  }
  if (!operatorEntry && (credential === "not-entitled" || credential === "unavailable")) {
    items.push({ id: "credential", text: t(failureMessageKey("credential_missing", credential)) });
  }
  return items;
}
